import { useState } from 'react';
import { AlertTriangle } from 'lucide-react';
import { CountryCityFields } from '@/components/shared/country-city-fields';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, Textarea } from '@/components/ui/misc';
import { FormError } from '@/features/auth/auth-layout';
import { useDebounce } from '@/hooks/use-debounce';
import { useUserOptions } from '@/hooks/use-user-options';
import { ApiRequestError } from '@/lib/api';
import { useDuplicateCheck, useSaveLead } from './api';
import { LEAD_SOURCE_LABELS, LEAD_STATUS_LABELS } from './labels';
import type { Lead, LeadSource, LeadStatus } from './types';

const SOURCES = Object.keys(LEAD_SOURCE_LABELS) as LeadSource[];
const STATUSES = (Object.keys(LEAD_STATUS_LABELS) as LeadStatus[]).filter(
  (status) => status !== 'CONVERTED',
);

export function LeadDialog({ lead, onClose }: { lead?: Lead; onClose: () => void }) {
  const save = useSaveLead();
  // The colleague directory, not the admin user list: a sales role may own a
  // lead without holding admin.user.view, and /users would answer them 403.
  const { data: users } = useUserOptions();
  const [error, setError] = useState<string | null>(null);

  const [form, setForm] = useState({
    firstName: lead?.firstName ?? '',
    lastName: lead?.lastName ?? '',
    companyName: lead?.companyName ?? '',
    email: lead?.email ?? '',
    phone: lead?.phone ?? '',
    whatsapp: lead?.whatsapp ?? '',
    designation: lead?.designation ?? '',
    source: lead?.source ?? ('OTHER' as LeadSource),
    industry: lead?.industry ?? '',
    country: lead?.country ?? '',
    city: lead?.city ?? '',
    budget: lead?.budget ?? '',
    requirement: lead?.requirement ?? '',
    status: lead?.status ?? ('NEW' as LeadStatus),
    unqualifiedReason: lead?.unqualifiedReason ?? '',
    ownerId: lead?.ownerId ?? '',
    assignmentRule: 'manual',
  });

  const set = (patch: Partial<typeof form>) => setForm((current) => ({ ...current, ...patch }));

  // Duplicate check runs against what has been typed so far (spec LEAD-02).
  const debouncedEmail = useDebounce(form.email, 500);
  const debouncedCompany = useDebounce(form.companyName, 500);
  const debouncedPhone = useDebounce(form.phone, 500);

  const { data: duplicates } = useDuplicateCheck({
    email: debouncedEmail,
    phone: debouncedPhone,
    companyName: debouncedCompany,
    excludeId: lead?.id,
  });

  const hits =
    (duplicates?.leads.length ?? 0) +
    (duplicates?.companies.length ?? 0) +
    (duplicates?.contacts.length ?? 0);

  const submit = async () => {
    setError(null);
    try {
      await save.mutateAsync({
        ...(lead ? { id: lead.id } : {}),
        firstName: form.firstName,
        lastName: form.lastName,
        companyName: form.companyName,
        email: form.email,
        phone: form.phone,
        whatsapp: form.whatsapp,
        designation: form.designation,
        source: form.source,
        industry: form.industry,
        country: form.country,
        city: form.city,
        budget: form.budget || undefined,
        requirement: form.requirement,
        status: form.status,
        unqualifiedReason: form.unqualifiedReason,
        ownerId: form.ownerId,
        ...(lead ? {} : { assignmentRule: form.assignmentRule }),
      });
      onClose();
    } catch (caught) {
      setError(caught instanceof ApiRequestError ? caught.message : 'Could not save the lead.');
    }
  };

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>{lead ? `Edit ${lead.number}` : 'New lead'}</DialogTitle>
          <DialogDescription>
            Capture the enquiry. The score is worked out from what you fill in.
          </DialogDescription>
        </DialogHeader>

        {hits > 0 && (
          <div className="rounded-md border border-amber-300 bg-amber-50 p-3">
            <p className="flex items-center gap-2 text-sm font-medium text-amber-900">
              <AlertTriangle className="h-4 w-4 shrink-0" />
              {hits === 1 ? 'A possible duplicate' : `${hits} possible duplicates`}
            </p>
            <ul className="mt-2 space-y-1 text-xs text-amber-800">
              {duplicates?.leads.map((duplicate) => (
                <li key={duplicate.id}>
                  Lead {duplicate.number} — {duplicate.name}
                  {duplicate.company ? ` (${duplicate.company})` : ''} · {duplicate.status}
                </li>
              ))}
              {duplicates?.companies.map((company) => (
                <li key={company.id}>Company — {company.name}</li>
              ))}
              {duplicates?.contacts.map((contact) => (
                <li key={contact.id}>
                  Contact — {contact.name}
                  {contact.email ? ` (${contact.email})` : ''}
                </li>
              ))}
            </ul>
            <p className="mt-2 text-xs text-amber-700">
              Check before saving — you can still continue if this is genuinely someone new.
            </p>
          </div>
        )}

        <div className="grid gap-4 sm:grid-cols-2">
          <Field label="First name">
            <Input
              value={form.firstName}
              autoFocus
              onChange={(event) => set({ firstName: event.target.value })}
            />
          </Field>
          <Field label="Last name">
            <Input value={form.lastName} onChange={(event) => set({ lastName: event.target.value })} />
          </Field>
          <Field label="Company">
            <Input
              value={form.companyName}
              onChange={(event) => set({ companyName: event.target.value })}
            />
          </Field>
          <Field label="Job title">
            <Input
              value={form.designation}
              onChange={(event) => set({ designation: event.target.value })}
            />
          </Field>
          <Field label="Email">
            <Input
              type="email"
              value={form.email}
              onChange={(event) => set({ email: event.target.value })}
            />
          </Field>
          <Field label="Phone">
            <Input value={form.phone} onChange={(event) => set({ phone: event.target.value })} />
          </Field>
          <Field label="WhatsApp">
            <Input
              value={form.whatsapp}
              placeholder="If different from phone"
              onChange={(event) => set({ whatsapp: event.target.value })}
            />
          </Field>
          <Field label="Source">
            <Select
              value={form.source}
              onChange={(event) => set({ source: event.target.value as LeadSource })}
            >
              {SOURCES.map((source) => (
                <option key={source} value={source}>
                  {LEAD_SOURCE_LABELS[source]}
                </option>
              ))}
            </Select>
          </Field>
          <Field label="Industry">
            <Input value={form.industry} onChange={(event) => set({ industry: event.target.value })} />
          </Field>
          <Field label="Indicative budget">
            <Input
              inputMode="decimal"
              placeholder="0.00"
              value={form.budget}
              onChange={(event) => set({ budget: event.target.value })}
            />
          </Field>
          <CountryCityFields
            className="sm:col-span-2"
            country={form.country}
            city={form.city}
            onChange={set}
          />

          <Field label="What do they need?" className="sm:col-span-2">
            <Textarea
              rows={3}
              value={form.requirement}
              placeholder="A fuller description scores higher and helps whoever picks this up."
              onChange={(event) => set({ requirement: event.target.value })}
            />
          </Field>

          <Field label="Status">
            <Select
              value={form.status}
              onChange={(event) => set({ status: event.target.value as LeadStatus })}
            >
              {STATUSES.map((status) => (
                <option key={status} value={status}>
                  {LEAD_STATUS_LABELS[status]}
                </option>
              ))}
            </Select>
          </Field>

          {form.status === 'UNQUALIFIED' && (
            <Field label="Why not qualified?">
              <Input
                value={form.unqualifiedReason}
                onChange={(event) => set({ unqualifiedReason: event.target.value })}
              />
            </Field>
          )}

          <Field label="Owner">
            <Select value={form.ownerId} onChange={(event) => set({ ownerId: event.target.value })}>
              <option value="">Assign automatically</option>
              {(users ?? []).map((user) => (
                <option key={user.id} value={user.id}>
                  {user.name}
                </option>
              ))}
            </Select>
          </Field>

          {!lead && !form.ownerId && (
            <Field label="Assignment rule">
              <Select
                value={form.assignmentRule}
                onChange={(event) => set({ assignmentRule: event.target.value })}
              >
                <option value="manual">Assign to me</option>
                <option value="round_robin">Round robin</option>
                <option value="load_balanced">Whoever has fewest open leads</option>
              </Select>
            </Field>
          )}
        </div>

        <FormError message={error} />

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={submit} disabled={save.isPending || form.firstName.trim().length === 0}>
            {save.isPending ? 'Saving…' : lead ? 'Save changes' : 'Create lead'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function Field({
  label,
  className,
  children,
}: {
  label: string;
  className?: string;
  children: React.ReactNode;
}) {
  return (
    <div className={`space-y-1.5 ${className ?? ''}`}>
      <Label>{label}</Label>
      {children}
    </div>
  );
}
